import React, { useCallback } from 'react';

import classnames from 'classnames';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { createSelector } from 'reselect';

import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Dropdown } from 'ui-components/components/INTERNAL/dropdown';
import { GuidService } from 'ui-components/services/guid-service';

import GenericFilterPill from '../../components/actionable-filter-pill/';
import ClearFilters from '../../components/filter-bar/clear-filters';
import FilterDropdownMenu from '../../components/filter-dropdown-menu/';
import FilterForm from '../../components/filter-form/';
import { AppliedFilterPropType } from '../../prop-types/applied-filter';
import { FilterOptionPropType } from '../../prop-types/filter-option';

import style from './filter-bar.module.less';
import messages from './messages';
import SavedFiltersDropdown from './saved-filters';
import FilterBarWithOverflow from './with-overflow';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

const NONE_GUID = '00000000-0000-0000-0000-000000000000';

const elementIdsThatCloseDropdown = ['filter-form-cancel', 'filter-form-apply', 'filter-form-remove'];

const trimStringFilterValues = (argument) => {
  if (Array.isArray(argument)) {
    return argument.map((v) => (typeof v === 'string' ? v.trim() : v));
  }
  return argument;
};

const shouldAllowDropdownToClose = (e) => {
  if (!e.target.id) {
    return false;
  }
  return elementIdsThatCloseDropdown.includes(e.target.id);
};

const shouldFilterAllowMultipleArguments = (filterBy) => filterBy?.allowsMultipleSelection;

const adjustCriterionOperatorForAllLikeFiltersIfItChanged = (localAppliedFilters = [], newFilterToAdd = {}, existingFilterIndex = -1) => {
  const hasCriterionChanged = localAppliedFilters[existingFilterIndex].criterionOperatorValue !== newFilterToAdd.criterionOperatorValue;
  if (!hasCriterionChanged) {
    return localAppliedFilters;
  }
  const newAppliedFilters = [...localAppliedFilters];
  if (!newFilterToAdd.criterionArgumentValue) {
    // the criterion changed so we need to retain the original value
    newAppliedFilters[existingFilterIndex] = {
      ...newFilterToAdd,
      criterionArgumentValue: localAppliedFilters[existingFilterIndex].criterionArgumentValue,
    };
  }
  return newAppliedFilters.map((filter) => ({
    ...filter,
    criterionOperatorValue:
      filter.filterById === newFilterToAdd.filterById ? newFilterToAdd.criterionOperatorValue : filter.criterionOperatorValue,
  }));
};

export const buildFilterObject = (filterBy, criterionOperator, criterionArgument, criterionMeta, appliedFilters = []) => {
  const filterIndex = appliedFilters.findIndex((f) => f.filterById === filterBy.id);
  return {
    id: shouldFilterAllowMultipleArguments(filterBy) && filterIndex >= 0 ? appliedFilters[filterIndex].id : GuidService.newGuid(),
    filterById: filterBy.id,
    type: filterBy.type,
    filterValue: filterBy.filterValue,
    criterionOperatorValue: get(criterionOperator, 'value'),
    criterionArgumentValue: trimStringFilterValues(get(criterionArgument, 'value')),
    criterionMeta,
  };
};

const buildBulkFilterObjects = (newFilterBy, newCriterionOperator, newCriterionArgument, newCriterionMeta, appliedFilters) => {
  if (newCriterionArgument && Array.isArray(newCriterionArgument)) {
    return newCriterionArgument.reduce((acc, criterionArgument) => {
      acc.push(buildFilterObject(newFilterBy, newCriterionOperator, criterionArgument, newCriterionMeta, [...appliedFilters, ...acc]));
      return acc;
    }, []);
  }
};

const getRequiredIndices = createSelector(
  (allFilterBys) => allFilterBys,
  (_allFilterBys, appliedFilters) => appliedFilters,
  (_, __, actionableFilterPills) => actionableFilterPills,
  (allFilterBys, appliedFilters, actionableFilterPills) => {
    const appliedFilterByIds = appliedFilters.map((filter) => filter.filterById);
    return actionableFilterPills.reduce((reqIndices, filterPill, index) => {
      if (appliedFilterByIds.includes(filterPill.props.filterById)) {
        reqIndices.push(index);
      }
      return reqIndices;
    }, []);
  },
);

const FilterBar = ({
  allFilterBys = emptyArray,
  popularFilterBys = emptyArray,
  otherFilterBys = emptyArray,
  onFiltersChanged,
  criteriaArgumentsMap = emptyObject,
  criteriaOperatorsMap = emptyObject,
  showFilterBar = true,
  className = '',
  appliedFilters = emptyArray,
  implicitFilterByIds = emptyArray,
  showOtherFilter = true,
  saveFilterPath,
  shouldUseOverflow = true,
  clearFilters,
}) => {
  const onApplyFilter = useCallback(
    (newFilterBy, newCriterionOperator, newCriterionArgument, newCriterionMeta, appliedFilter) => {
      if (Array.isArray(newCriterionArgument)) {
        const bulkFiltersToAdd = buildBulkFilterObjects(
          newFilterBy,
          newCriterionOperator,
          newCriterionArgument,
          newCriterionMeta,
          appliedFilters,
        );
        const appliedNotMatchingFilters = appliedFilters.filter((filterObject) => filterObject.filterById !== newFilterBy.id);
        const filtersToApply = [...appliedNotMatchingFilters, ...bulkFiltersToAdd];
        return onFiltersChanged(filtersToApply);
      }

      const singleFilterToAdd = buildFilterObject(
        newFilterBy,
        newCriterionOperator,
        newCriterionArgument,
        newCriterionMeta,
        appliedFilters,
      );
      // for filters that do not allow multiple values, we need to replace any
      // previous instances of that filter with the new filter values
      if (!shouldFilterAllowMultipleArguments(newFilterBy)) {
        if (appliedFilter) {
          return onFiltersChanged([...appliedFilters.filter((filter) => filter !== appliedFilter), singleFilterToAdd]);
        }
        return onFiltersChanged([...appliedFilters, singleFilterToAdd]);
      }

      const existingFilterIndex = appliedFilters.findIndex((filter) => filter.filterById === newFilterBy.id);

      if (existingFilterIndex === -1) {
        const filtersToApply = [...appliedFilters, singleFilterToAdd];
        return onFiltersChanged(filtersToApply);
      }

      if (shouldFilterAllowMultipleArguments(newFilterBy)) {
        const appliedMatchingFilters = appliedFilters.filter((filterObject) => filterObject.filterById === newFilterBy.id);
        const appliedNonMatchingFilters = appliedFilters.filter((filterObject) => filterObject.filterById !== newFilterBy.id);
        const newFilterIsDuped = appliedMatchingFilters.find(
          (filterObject) => filterObject.criterionArgumentValue === singleFilterToAdd.criterionArgumentValue,
        );

        if (newFilterIsDuped) {
          return onFiltersChanged([...appliedFilters]);
        }

        // NONE_GUID checks
        let workingAppliedFilters = [...appliedFilters];
        const isNewFilterNone = singleFilterToAdd.criterionArgumentValue === NONE_GUID;
        const appliedNoneFilter = appliedMatchingFilters.find((filter) => filter.criterionArgumentValue === NONE_GUID);

        if (isNewFilterNone) {
          return onFiltersChanged([...appliedNonMatchingFilters, singleFilterToAdd]);
        }
        if (appliedNoneFilter) {
          workingAppliedFilters = workingAppliedFilters.filter((filter) => filter.id !== appliedNoneFilter.id);
        }
        return onFiltersChanged([...workingAppliedFilters, singleFilterToAdd]);
      }

      const filtersToApply = adjustCriterionOperatorForAllLikeFiltersIfItChanged(appliedFilters, singleFilterToAdd, existingFilterIndex);
      return onFiltersChanged(filtersToApply);
    },
    [appliedFilters, onFiltersChanged],
  );

  const onRemoveFilter = useCallback(
    (removedFilters) => {
      if (Array.isArray(removedFilters)) {
        const removedMap = removedFilters.reduce((obj, filter) => {
          return {
            ...obj,
            [filter.criterionArgumentValue]: filter,
          };
        }, {});

        const filtersToApply = appliedFilters.filter((filterObject) => !removedMap[filterObject.criterionArgumentValue]);
        return onFiltersChanged(filtersToApply);
      }
      const filtersToApply = appliedFilters.filter((filterObject) => {
        return removedFilters.criterionArgumentValue !== filterObject.criterionArgumentValue;
      });
      return onFiltersChanged(filtersToApply);
    },
    [appliedFilters, onFiltersChanged],
  );

  if (!showFilterBar) {
    return null;
  }
  const renderFilterButtonDropdownMenu = (filterBy, appliedFilterId) => {
    return (
      <FilterForm
        appliedFilterId={appliedFilterId}
        appliedFilters={appliedFilters}
        className={style['popular-filter-form']}
        criterionArgumentOptions={criteriaArgumentsMap[filterBy.id]}
        criterionOperatorOptions={criteriaOperatorsMap[filterBy.type]}
        onApplyFilter={onApplyFilter}
        onRemoveFilter={onRemoveFilter}
        selectedFilterBy={filterBy}
        shouldShowHeader={false}
      />
    );
  };

  const renderSingleActionableFilterPill = (filterBy, index, appliedFilterId) => {
    const dropdownContent = renderFilterButtonDropdownMenu(filterBy, appliedFilterId);
    const displayContent = filterBy.displayName || null;

    if (filterBy.actionableFilterPill) {
      const ProvidedFilterPill = filterBy.actionableFilterPill;
      return (
        <ProvidedFilterPill
          key={`${filterBy.id}-${index}`}
          containerAllowedMeToClose={shouldAllowDropdownToClose}
          defaultDisplayContent={displayContent}
          dropdownContent={dropdownContent}
          filterById={filterBy.id}
        />
      );
    }
    return (
      <GenericFilterPill
        key={`${filterBy.id}-${index}`}
        containerAllowedMeToClose={shouldAllowDropdownToClose}
        displayContent={displayContent}
        dropdownContent={dropdownContent}
        filterById={filterBy.id}
        isSelected={appliedFilters.some((filter) => filter.filterById === filterBy.id)}
      />
    );
  };

  const renderActionableFilterPills = (filterBy, renderAll) => {
    const allActiveFilterIds = [...appliedFilters.map((filter) => filter.filterById), ...implicitFilterByIds];
    const isActive = allActiveFilterIds.includes(filterBy.id);
    const isPopular = popularFilterBys.map((filter) => filter.id).includes(filterBy.id);
    if (!renderAll && !isActive && !isPopular) {
      return [null];
    }
    if (filterBy.hideWhenInactive && !appliedFilters.find(({ filterById }) => filterById === filterBy.id)) {
      return [<span key={filterBy.id} />]; // preserve for measuring in overflow
    }
    const appliedFiltersForFilterBy = appliedFilters.filter((af) => af.filterById === filterBy.id);
    if (appliedFiltersForFilterBy.length === 0 || shouldFilterAllowMultipleArguments(filterBy)) {
      return [renderSingleActionableFilterPill(filterBy, 0, undefined)];
    }
    return appliedFiltersForFilterBy.map((af, index) => renderSingleActionableFilterPill(filterBy, index, af.id));
  };

  const renderOtherFilters = (dropdownFilterByOverrides) => (
    <Dropdown key='other-filters' closeWhenChildrenClicked={false} containerAllowedMeToClose={shouldAllowDropdownToClose}>
      <DropdownMenuToggler className={style['other-filter-options-toggle']} theme='link'>
        <FormattedMessage {...messages.otherFiltersButtonText} />
      </DropdownMenuToggler>
      <div>
        <FilterDropdownMenu
          appliedFilters={appliedFilters}
          criteriaArgumentsMap={criteriaArgumentsMap}
          criteriaOperatorsMap={criteriaOperatorsMap}
          onApplyFilter={onApplyFilter}
          onRemoveFilter={onRemoveFilter}
          otherFilterBys={dropdownFilterByOverrides || otherFilterBys}
        />
      </div>
    </Dropdown>
  );

  const nonLockedFilters = appliedFilters?.filter((f) => !f.isLocked);

  const actionablePills = allFilterBys.reduce((acc, filterBy) => {
    acc.push(...renderActionableFilterPills(filterBy, shouldUseOverflow));
    return acc;
  }, []);

  return (
    <div className={classnames(shouldUseOverflow ? style['overflow-bar-wrapper'] : style['filter-bar-wrapper'], className)}>
      <div className={style['filter-by-label']}>
        <FormattedMessage {...messages.filterBy} />
      </div>
      {shouldUseOverflow && (
        <div className={style['filters-with-overflow']} data-testid='filter-bar-overflow'>
          <FilterBarWithOverflow
            allFilterBys={allFilterBys}
            appliedFilters={appliedFilters}
            clearFilters={clearFilters}
            popularFilterBys={popularFilterBys}
            renderOtherFilters={renderOtherFilters}
            requiredIndices={getRequiredIndices(allFilterBys, appliedFilters, actionablePills)}
            saveFilterPath={saveFilterPath}
          >
            {actionablePills}
          </FilterBarWithOverflow>
        </div>
      )}
      {!shouldUseOverflow && (
        <div className={style['dropdowns-wrapper']}>
          {actionablePills}
          {showOtherFilter && renderOtherFilters()}
          {nonLockedFilters && nonLockedFilters.length > 0 && <ClearFilters clearFilters={clearFilters} />}
          <SavedFiltersDropdown appliedFilters={appliedFilters} path={saveFilterPath} />
        </div>
      )}
    </div>
  );
};
FilterBar.propTypes = {
  allFilterBys: PropTypes.arrayOf(FilterOptionPropType),
  popularFilterBys: PropTypes.arrayOf(FilterOptionPropType),
  otherFilterBys: PropTypes.arrayOf(FilterOptionPropType),
  onFiltersChanged: PropTypes.func.isRequired,
  criteriaArgumentsMap: PropTypes.object,
  criteriaOperatorsMap: PropTypes.object,
  showFilterBar: PropTypes.bool,
  className: PropTypes.string,
  appliedFilters: PropTypes.arrayOf(AppliedFilterPropType),
  implicitFilterByIds: PropTypes.array,
  showOtherFilter: PropTypes.bool,
  saveFilterPath: PropTypes.string,
  shouldUseOverflow: PropTypes.bool,
  clearFilters: PropTypes.func.isRequired,
};

export default FilterBar;
